import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Snackbars from "components/Snackbar/Snackbar.js";
import { downloadReport, getReport } from "./downloadRequest";
import { LoadingComponent } from "components/Loading/LoadingComponent";
import { useSearch } from "hooks/useSearch";
import { GlobalBox } from "components/Box/GlobalBox";
import { Autocomplete, Grid, TextField, Typography } from "@mui/material";
import { animations } from "helpers/animations";
import { useForm } from "hooks/useForm";
import { fieldValidations, initialValues } from "./inputValues";
import { Box, Card, Chip, makeStyles } from "@material-ui/core";
import RegularButton from "components/CustomButtons/Button";
import { Search } from "@material-ui/icons";
import { SearchRounded } from "@mui/icons-material";
import { GN3 } from "./columns";
import CardTable from "components/CardTable/CardTable";
import styles from "./styles";
import { GasMovTableHeaders } from "./gasMovTableHeaders";
import { monthsOptions, yearsOptions } from "./dateValues";
import { formatterNumberDecimals } from "helpers/mathDictionary";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";

const useStyles = makeStyles(styles);
const base = {
  rfc: '',
  satTotal: { lt: 0, kg: 0, total: 0 },
  gn3Total: { lt: 0, kg: 0, total: 0 },
  difference: { lt: 0, kg: 0, total: 0 },
  gn3Registers: [],
}

export const GasMovReportPage = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { setSnackbar, clearSnackbar, clearSearch } = useSearch();

  const {
    inputValues,
    handleNewValues,
    setValues,
  } = useForm(initialValues, fieldValidations);
  const { companie, stations, rfc, year, months } = inputValues;
  const { token } = useSelector((state) => state.login);
  const { color, message, isOpen, isLoading } = useSelector((state) => state.search);
  const { companiesList } = useSelector((state) => state.companies);
  const [stationsOptions, setStationsOptions] = useState([]);
  const [data, setData] = useState(base)

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!rfc) {
      setSnackbar('Selecciona una compañía')
      return
    }
    if (!stations.length) {
      setSnackbar('Selecciona una planta')
      return
    }
    const resp = await getReport(
      parseInt(year.value),
      months.map((v) => parseInt(v.value)).sort((a, b) => a - b),
      rfc,
      stations.map((v) => v.value),
      dispatch
    )
    setData(resp ? resp : base)
  }, [year, months, rfc, stations, setSnackbar, token, dispatch]);

  const handleGenerate = useCallback((e) => {
    e.preventDefault();
    dispatch(downloadReport(
      parseInt(year.value),
      months.map((v) => parseInt(v.value)).sort((a, b) => a - b),
      rfc,
      stations.map((v) => v.value),
      token
    ));
  }, [year, months, rfc, stations, setSnackbar, token, dispatch]);

  const handleYearChange = (v) => {
    handleNewValues({
      year: v
    })
  }

  const handleMonthsChange = (v) => {
    let value = v
    const sortedMonths = v.map((v) => parseInt(v.value)).sort((a, b) => a - b)

    if (((sortedMonths[sortedMonths.length - 1] - sortedMonths[0]) + 1) > sortedMonths.length && sortedMonths.length < sortedMonths[sortedMonths.length - 1])
      value = monthsOptions.slice(sortedMonths[0] - 1, sortedMonths[sortedMonths.length - 1])

    handleNewValues({
      months: value
    })
  }

  const handleCompanieChange = (v) => {
    handleNewValues({
      companie: v?.id,
      stations: [],
      rfc: v?.rfc
    })

    if (v?.stations) setStationsOptions(v.stations)
    else setStationsOptions([])
  };

  useEffect(() => {
    clearSearch();
  }, [clearSearch]);

  return (
    <>
      <GlobalBox icon={<Search />} title={"Buscar reportes de movimientos"}>
        <Box>
          <Grid container spacing={2}>
            <Grid item sx={{ alignSelf: 'center' }} xs={12} md={2} className={animations.fadeIn}>
              <Autocomplete
                limitTags={1}
                options={yearsOptions}
                value={year}
                filterSelectedOptions
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    value={year}
                    label="Año"
                    variant="outlined"
                    size="small"
                  />
                )}
                fullWidth
                name="year"
                onChange={(e, v) => handleYearChange(v)}
                noOptionsText="No hay años disponibles"
              />
            </Grid>
            <Grid item sx={{ alignSelf: 'center' }} xs={12} md={3} className={animations.fadeIn}>
              <Autocomplete
                limitTags={1}
                options={year?.value === new Date().getFullYear().toString()
                  ? monthsOptions.filter(monthOption => monthOption.value - 1 < new Date().getMonth())
                  : monthsOptions
                }
                value={months}
                filterSelectedOptions
                getOptionLabel={(option) => option.label}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                      style={{
                        borderRadius: 4,
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    value={months}
                    required={true}
                    label="Meses"
                    variant="outlined"
                    size="small"
                  />
                )}
                fullWidth
                name="months"
                onChange={(e, v) => handleMonthsChange(v)}
                noOptionsText="No hay meses disponibles"
                multiple
              />
            </Grid>
            <Grid item sx={{ alignSelf: 'center' }} xs={12} md={3} className={animations.fadeIn}>
              <Autocomplete
                limitTags={1}
                options={companiesList}
                filterSelectedOptions
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    value={companie}
                    label="Compañia"
                    variant="outlined"
                    size="small"
                  />
                )}
                fullWidth
                name="companie"
                onChange={(e, v) => handleCompanieChange(v)}
                noOptionsText="No hay compañías disponibles"
              />
            </Grid>
            <Grid item sx={{ alignSelf: 'center' }} xs={11} md={3} className={animations.fadeIn}>
              <AutocompleteComponent
                label="Plantas"
                name="stations"
                value={stations}
                handleValues={setValues}
                fullWidth
                variant="outlined"
                allLabel='Todas las plantas'
                options={stationsOptions}
                multiple= {true}
                enableSelectAll = {true}
                size="small"
              />
            </Grid>
            <Grid item xs={1} className={`${animations.fadeIn} ${classes.buttonSearch}`}>
              <RegularButton justIcon round color="info" onClick={handleSubmit} disabled={isLoading}>
                <SearchRounded />
              </RegularButton>
            </Grid>
          </Grid>
        </Box>
        <Box>
          <Grid container spacing={4} sx={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <CardTable
                data={data.gn3Registers}
                columns={GN3}
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box className={classes.tablesContainer}>
                {/* GN3 */}
                <Card className={classes.tableBackground}>
                  <Grid container spacing={0}>
                    <GasMovTableHeaders title={"CFDI SALIDAS"} titles={["", "Litros", "Kilos", "Importe"]} />
                    <Grid item xs={3} className={classes.cell}>
                      <Typography className={classes.headerText}>
                        TOTALES
                      </Typography>
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      {formatterNumberDecimals(data.gn3Total.lt, 2)}
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      {formatterNumberDecimals(data.gn3Total.kg, 2)}
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      ${formatterNumberDecimals(data.gn3Total.total, 2)}
                    </Grid>
                  </Grid>
                </Card>

                {/* SAT */}
                <Card className={classes.tableBackground}>
                  <Grid container spacing={0}>
                    <GasMovTableHeaders title={"CFDI SAT"} titles={["RFC", "Litros", "Kilos", "Importe"]} />
                    <Grid item xs={3} className={classes.cell}>
                      {data.rfc}
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      {formatterNumberDecimals(data.satTotal.lt, 2)}
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      {formatterNumberDecimals(data.satTotal.kg, 2)}
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      ${formatterNumberDecimals(data.satTotal.total, 2)}
                    </Grid>
                  </Grid>
                </Card>

                {/* COMPARATION */}
                <Card className={classes.tableBackground}>
                  <Grid container spacing={0}>
                    <GasMovTableHeaders title={"DIFERENCIAS"} titles={["", "Litros", "Kilos", "Importe"]} />
                    <Grid item xs={3} className={classes.cell}>
                      <Typography className={classes.headerText}>
                        TOTALES
                      </Typography>
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      {formatterNumberDecimals(data.difference.lt, 2)}
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      {formatterNumberDecimals(data.difference.kg, 2)}
                    </Grid>
                    <Grid item xs={3} className={classes.cell}>
                      ${formatterNumberDecimals(data.difference.total, 2)}
                    </Grid>
                  </Grid>
                </Card>
                <Box sx={{ alignSelf: 'center', width: '40%' }} >
                  <RegularButton fullWidth color="info" onClick={handleGenerate} disabled={!data.rfc}>
                    Generar XML
                  </RegularButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </GlobalBox>
      {isLoading && <LoadingComponent label="Generando XLSX" />}
      <Snackbars
        place="tr"
        color={color}
        message={message}
        open={isOpen}
        closeNotification={clearSnackbar}
        close
      />
    </>
  );
};
